import { useCallback } from "react";
import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import { isFailed, State, Step } from "~/lib/pipeline";
import { StepItem } from "~/components/build/Show/components/Sidebar/TreeNode/StepItem";
import { twMerge } from "tailwind-merge";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

interface Props {
  step: Step;
  collapsibleState?: CollapsibleState;
  depth?: number;
  isLoading?: boolean;
}

export function CollapsibleStepItem({
  step,
  depth = 0,
  collapsibleState = CollapsibleState.Collapsed,
  isLoading = false,
}: Props) {
  const params = useQueryParams();
  const toggleStep = useBuildStore((state) => state.toggleStep);
  const active = params.stepUuid === step.uuid;
  const isExpanded =
    collapsibleState === CollapsibleState.Expanded || collapsibleState === CollapsibleState.Full;

  const open = useCallback(() => {
    toggleStep(step.uuid);
  }, []);

  return (
    <>
      <StepItem
        step={step}
        active={active}
        depth={depth}
        // Highlight step as failed only when it's failed and the group is collapsed.
        failed={isFailed(step) && !isExpanded}
        className={classNames({
          "animate-pulse": isLoading,
          "pl-[calc(1.5rem_+_2px)]": depth === 0, // + 2px to account for chevron padding
          "pl-[calc(2.5rem_+_4px)]": depth === 1, // + 4px to account for chevron padding
        })}
      />

      <div
        role="button"
        onClick={open}
        tabIndex={-1}
        data-testid="collapsible-step-toggle"
        className={twMerge(
          classNames(
            "absolute bottom-[2px] top-[2px] flex w-6 cursor-pointer select-none items-center justify-center rounded-sm hover:hover:bg-[#CDC6FF]",
            {
              "left-[2px]": depth === 0,
              "left-[calc(1rem_+_4px)]": depth === 1,
              "opacity-45 hover:opacity-100":
                step.state === State.WaitingForDependencies && !active,
            },
          ),
        )}
      >
        <Icon
          icon="heroicons/outline/chevron-right"
          className={classNames("h-3 w-3 transition-transform duration-150", {
            "rotate-90": isExpanded,
          })}
        />
      </div>
    </>
  );
}
